import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Properties, formatINR, formatINRFull } from "../lib/api";
import { ArrowLeft, Building2, Calendar, FileCheck2, MapPin, ShieldCheck, Share2, Download, Phone, MessageCircle } from "lucide-react";
import WealthCalculator from "../components/WealthCalculator";
import { toast } from "sonner";

const WHATSAPP_NUMBER = "918758115155";

export default function PropertyDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);

  useEffect(() => {
    Properties.byId(id).then(setP).catch(() => setP(null));
  }, [id]);

  const code = p ? (p.code || `ZPL${(p.id || "").slice(0, 4).toUpperCase()}`) : "";

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: code, url }); } catch (_) {}
    } else {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    }
  };

  const downloadPitch = () => { window.print(); };

  if (!p) return <div className="container-zelo py-20" data-testid="property-detail-loading">Loading…</div>;

  const pricePerSBA = p.asking_price / p.super_built_up_area;
  const pricePerCarpet = p.asking_price / p.carpet_area;

  return (
    <div className="bg-white" data-testid="property-detail-page">
      <div className="container-zelo py-8">
        <Link to="/properties" className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-brand transition" data-testid="back-to-listings">
          <ArrowLeft className="w-4 h-4" /> Back to all properties
        </Link>
      </div>

      <div className="container-zelo grid grid-cols-1 lg:grid-cols-12 gap-8 pb-16">
        <div className="lg:col-span-7">
          <div className="rounded-2xl overflow-hidden bg-neutral-100 aspect-[4/3] relative">
            <img src={p.image} alt="" className="w-full h-full object-cover" />
            <div className="absolute bottom-4 left-4 inline-flex items-center gap-2 bg-white/95 backdrop-blur px-4 py-2 rounded-full shadow-soft">
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              <span className="text-brand font-bold">{p.roi}% Yield</span>
            </div>
            <div className="absolute top-4 left-4 bg-white/85 backdrop-blur px-2 py-0.5 rounded-md text-[10px] font-mono font-medium tracking-wider text-neutral-500 shadow-soft">
              {code}
            </div>
          </div>

          <div className="mt-6">
            <div className="font-heading text-3xl md:text-4xl font-medium tracking-tight">{p.tenant_industry}</div>
            <div className="text-base text-neutral-500 mt-1">{p.property_type}</div>
            <div className="flex items-center gap-2 text-neutral-500 mt-3 text-sm">
              <MapPin className="w-4 h-4" /> {p.area}, {p.city}
            </div>
          </div>

          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-4">
            <Spec l="Property Price" v={formatINR(p.asking_price)} />
            <Spec l="Monthly Rent" v={formatINR(p.monthly_rent)} accent />
            <Spec l="Tenant Industry" v={p.tenant_industry} />
            <Spec l="Carpet Area" v={`${p.carpet_area.toLocaleString("en-IN")} sqft`} />
            <Spec l="Super Built Up" v={`${p.super_built_up_area.toLocaleString("en-IN")} sqft`} />
            <Spec l="Floor" v={p.floor} />
            <Spec l="Lease Period" v={`${p.lease_period_years} years`} />
            <Spec l="Lock-in" v={`${p.lockin_years} years`} />
            <Spec l="Escalation" v={`${p.rent_escalation_percent}% / ${p.escalation_every_years} yrs`} />
            <Spec l="Lease Start" v={new Date(p.lease_start).toLocaleDateString("en-IN")} />
            <Spec l="Property Tax" v={p.property_tax_paid_by} />
            <Spec l="Maintenance" v={p.maintenance_paid_by} />
          </div>

          <div className="mt-8 rounded-2xl border border-neutral-200 bg-neutral-50 p-6">
            <div className="label-tiny text-brand">Purchase Price</div>
            <div className="grid grid-cols-2 gap-4 mt-3">
              <div>
                <div className="text-sm text-neutral-500">On Super Built Up</div>
                <div className="font-heading text-2xl font-medium mt-1">{formatINRFull(Math.round(pricePerSBA))}<span className="text-sm text-neutral-400 font-normal"> /sqft</span></div>
              </div>
              <div>
                <div className="text-sm text-neutral-500">On Carpet</div>
                <div className="font-heading text-2xl font-medium mt-1">{formatINRFull(Math.round(pricePerCarpet))}<span className="text-sm text-neutral-400 font-normal"> /sqft</span></div>
              </div>
            </div>
          </div>

          {p.description && (
            <div className="mt-8">
              <div className="label-tiny text-brand">About this asset</div>
              <p className="mt-3 text-neutral-700 leading-relaxed">{p.description}</p>
            </div>
          )}

          <div className="mt-10">
            <div className="label-tiny text-brand mb-4">Run the numbers</div>
            <WealthCalculator initialPrice={p.asking_price} initialRent={p.monthly_rent} />
          </div>
        </div>

        {/* Sticky right rail */}
        <aside className="lg:col-span-5">
          <div className="lg:sticky lg:top-24">
            <div className="rounded-2xl border border-neutral-200 bg-white shadow-soft overflow-hidden">
              <div className="bg-gradient-to-br from-brand to-brand-600 text-white p-6">
                <div className="text-xs uppercase tracking-[0.18em] opacity-80">Asking Price</div>
                <div className="font-heading text-4xl font-medium mt-1">{formatINR(p.asking_price)}</div>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <Mini l="Monthly Rent" v={formatINR(p.monthly_rent)} />
                  <Mini l="ROI" v={`${p.roi}%`} />
                  <Mini l="Lease" v={`${p.lease_period_years} yrs`} />
                  <Mini l="Lock-in" v={`${p.lockin_years} yrs`} />
                </div>
              </div>
              <div className="p-6 space-y-3">
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Hi Zelo, I'm interested in ${code} (${p.area}, ${p.city})`)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 text-white px-5 py-3 text-sm font-semibold hover:bg-emerald-600 transition"
                  data-testid="detail-whatsapp-btn"
                >
                  <MessageCircle className="w-4 h-4" /> Enquire on WhatsApp
                </a>
                <a href={`tel:+${WHATSAPP_NUMBER}`} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white px-5 py-3 text-sm font-semibold hover:bg-brand-600 transition" data-testid="detail-call-btn">
                  <Phone className="w-4 h-4" /> Call Investment Desk
                </a>
                <div className="grid grid-cols-2 gap-2 pt-2">
                  <button onClick={share} className="inline-flex items-center justify-center gap-2 rounded-full border border-neutral-200 px-4 py-2.5 text-sm font-semibold hover:border-brand hover:text-brand transition" data-testid="detail-share-btn">
                    <Share2 className="w-4 h-4" /> Share
                  </button>
                  <button onClick={downloadPitch} className="inline-flex items-center justify-center gap-2 rounded-full border border-neutral-200 px-4 py-2.5 text-sm font-semibold hover:border-brand hover:text-brand transition" data-testid="detail-download-btn">
                    <Download className="w-4 h-4" /> Pitch Deck
                  </button>
                </div>
              </div>
              <div className="border-t border-neutral-100 p-6 space-y-3">
                <Trust Icon={ShieldCheck} t="Verified · Leased · Documented" />
                <Trust Icon={Calendar} t={`Lease starts ${new Date(p.lease_start).toLocaleDateString("en-IN")}`} />
                <Trust Icon={FileCheck2} t={`Security Deposit: ${formatINR(p.security_deposit)}`} />
                <Trust Icon={Building2} t={`${p.area}, ${p.city}`} />
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Spec({ l, v, accent }) {
  return (
    <div className={`rounded-2xl border ${accent ? "border-brand/40 bg-brand-50" : "border-neutral-200 bg-white"} p-4`}>
      <div className="label-tiny">{l}</div>
      <div className={`font-heading text-lg font-medium tracking-tight mt-1 ${accent ? "text-brand-700" : "text-neutral-900"}`}>{v}</div>
    </div>
  );
}

function Mini({ l, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] opacity-70">{l}</div>
      <div className="font-medium text-sm mt-0.5">{v}</div>
    </div>
  );
}

function Trust({ Icon, t }) {
  return (
    <div className="flex items-center gap-3 text-sm text-neutral-600">
      <span className="w-8 h-8 rounded-lg bg-brand-50 text-brand flex items-center justify-center"><Icon className="w-4 h-4" /></span>
      <span>{t}</span>
    </div>
  );
}
